import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Save, MapPin, Globe, Mail, Phone, Map } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";

const DEFAULT = {
  address_line: "",
  web_line: "",
  email: "",
  phone: "",
  map_lat: 42.598,
  map_lng: -5.568,
  map_zoom: 15,
  shipping_peninsula: 4.95,
  shipping_baleares: 8.95,
  shipping_canarias_cm: 12.95,
  free_shipping_from: 60.0,
};

export default function AdminSettings() {
  const [form, setForm] = useState(DEFAULT);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/admin/settings")
      .then((r) => setForm({ ...DEFAULT, ...r.data }))
      .catch(() => toast.error("No se pudieron cargar los ajustes"))
      .finally(() => setLoading(false));
  }, []);

  const set = (k) => (e) =>
    setForm((f) => ({ ...f, [k]: e.target.value }));

  const setNum = (k) => (e) =>
    setForm((f) => ({ ...f, [k]: parseFloat(e.target.value) || 0 }));

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.put("/admin/settings", form);
      toast.success("Ajustes guardados correctamente");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setSaving(false);
    }
  };

  // Build OSM embed URL from current lat/lng
  const delta = 0.015;
  const { map_lat: lat, map_lng: lng } = form;
  const bbox = `${lng - delta}%2C${lat - delta}%2C${lng + delta}%2C${lat + delta}`;
  const mapSrc = `https://www.openstreetmap.org/export/embed.html?bbox=${bbox}&layer=mapnik&marker=${lat}%2C${lng}`;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-40">
        <span className="tracking-eyebrow text-[var(--eafg-muted)]">Cargando…</span>
      </div>
    );
  }

  return (
    <div className="max-w-3xl fade-in">
      <p className="tracking-eyebrow text-[var(--eafg-muted)] mb-1">Administración</p>
      <h1 className="font-serif-display text-4xl mb-10">Ajustes de contacto</h1>

      <form onSubmit={save} className="space-y-10">

        {/* Contact info */}
        <section className="border border-[var(--eafg-line)] p-8 bg-white space-y-6">
          <h2 className="tracking-eyebrow text-[var(--eafg-muted)]">Información de contacto</h2>

          <Field
            icon={<MapPin size={14} strokeWidth={1.5} />}
            label="Dirección (texto visible)"
            value={form.address_line}
            onChange={set("address_line")}
            placeholder="C/ del Taller, 12 · León, España"
          />
          <Field
            icon={<Globe size={14} strokeWidth={1.5} />}
            label="Web"
            value={form.web_line}
            onChange={set("web_line")}
            placeholder="esmaltealfuegoguerrero.com"
          />
          <Field
            icon={<Mail size={14} strokeWidth={1.5} />}
            label="Email de contacto"
            type="email"
            value={form.email}
            onChange={set("email")}
            placeholder="hola@esmaltealfuegoguerrero.com"
          />
          <Field
            icon={<Phone size={14} strokeWidth={1.5} />}
            label="Teléfono (opcional)"
            value={form.phone}
            onChange={set("phone")}
            placeholder="+34 987 000 000"
          />
        </section>

        {/* Map */}
        <section className="border border-[var(--eafg-line)] p-8 bg-white space-y-6">
          <h2 className="tracking-eyebrow text-[var(--eafg-muted)]">
            <Map size={14} strokeWidth={1.5} className="inline mr-2" />
            Posición del mapa
          </h2>
          <p className="text-sm text-[var(--eafg-muted)]">
            Introduce las coordenadas del punto que quieres mostrar. Puedes obtenerlas en{" "}
            <a
              href="https://www.openstreetmap.org"
              target="_blank"
              rel="noreferrer"
              className="underline hover:text-[var(--eafg-copper)]"
            >
              openstreetmap.org
            </a>{" "}
            haciendo clic derecho → «Mostrar dirección».
          </p>

          <div className="grid grid-cols-2 gap-4">
            <Field
              label="Latitud"
              type="number"
              step="0.0001"
              value={form.map_lat}
              onChange={setNum("map_lat")}
              placeholder="42.5980"
            />
            <Field
              label="Longitud"
              type="number"
              step="0.0001"
              value={form.map_lng}
              onChange={setNum("map_lng")}
              placeholder="-5.5680"
            />
          </div>

          {/* Live preview */}
          <div>
            <p className="tracking-eyebrow text-[var(--eafg-muted)] text-[0.7rem] mb-3">Vista previa del mapa</p>
            <div className="aspect-[16/7] border border-[var(--eafg-line)] overflow-hidden">
              <iframe
                key={mapSrc}
                title="Vista previa mapa"
                src={mapSrc}
                className="w-full h-full"
                loading="lazy"
              />
            </div>
          </div>
        </section>

        {/* Envíos */}
        <section className="border border-[var(--eafg-line)] p-8 bg-white space-y-6">
          <h2 className="tracking-eyebrow text-[var(--eafg-muted)]">Gastos de envío</h2>
          <p className="text-sm text-[var(--eafg-muted)]">
            Tarifa plana por zona (IVA incluido). La zona se detecta automáticamente
            por el código postal del cliente. Referencia: Correos Paq Estándar.
          </p>
          <div className="grid grid-cols-2 gap-4">
            <Field
              label="Península (€)"
              type="number" step="0.01"
              value={form.shipping_peninsula}
              onChange={setNum("shipping_peninsula")}
            />
            <Field
              label="Baleares (€)"
              type="number" step="0.01"
              value={form.shipping_baleares}
              onChange={setNum("shipping_baleares")}
            />
            <Field
              label="Canarias / Ceuta / Melilla (€)"
              type="number" step="0.01"
              value={form.shipping_canarias_cm}
              onChange={setNum("shipping_canarias_cm")}
            />
            <Field
              label="Envío gratis a partir de (€, 0 = nunca)"
              type="number" step="1"
              value={form.free_shipping_from}
              onChange={setNum("free_shipping_from")}
            />
          </div>
        </section>

        <button
          type="submit"
          disabled={saving}
          className="flex items-center gap-2 bg-[var(--eafg-ink)] text-white px-7 py-4 tracking-eyebrow hover:bg-[var(--eafg-copper)] transition-colors disabled:opacity-50"
        >
          <Save size={15} strokeWidth={1.5} />
          {saving ? "Guardando…" : "Guardar ajustes"}
        </button>
      </form>
    </div>
  );
}

function Field({ icon, label, value, onChange, type = "text", placeholder = "", step }) {
  return (
    <label className="block">
      <span className="tracking-eyebrow text-[var(--eafg-muted)] text-[0.72rem] flex items-center gap-2 mb-2">
        {icon} {label}
      </span>
      <input
        type={type}
        step={step}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        className="w-full border-b border-[var(--eafg-line)] bg-transparent py-2 focus:outline-none focus:border-[var(--eafg-ink)] text-sm"
      />
    </label>
  );
}
